// src/components/quotes/roadmapQuote.js
//
// Seed a quote from a roadmap item — "Create quote" on the Recommendations &
// roadmap view.
//
// ⚠ REUSES makeQuoteRecord, deliberately. quoteRecord.js exists because quotes
// once had THREE independent object literals and the Q number reached only two
// of them, quietly minting unnumbered quotes from the third. A fourth literal
// here would be exactly that mistake again — so this module only maps a roadmap
// item onto the factory's arguments and never builds a record itself.
//
// Kept React-free so it can be unit-tested, the same reason onboardingQuotes.js
// is.
import { makeQuoteRecord } from "./quoteRecord";

/* A roadmap item is one thing to do at one price, so the quote gets one
   section holding one option holding one line. That is the minimum shape the
   builder renders without an empty state — a section with options but no
   selectedOptionId shows as "nothing chosen", which is why the option is
   selected here rather than left to the builder. */
export async function makeQuoteFromRoadmapItem(item, {
  companyId = "",
  companyName = "",
  contactName = "",
  contactEmail = "",
  now = Date.now(),
  seed,
  reserve,
} = {}) {
  if (!item || !String(item.title || "").trim()) {
    throw new Error("A roadmap item needs a title before it can be quoted.");
  }
  const key = seed != null ? seed : now;
  const sectionId = `sec-${key}`;
  const optionId  = `opt-${key}`;

  /* ⚠ The COST IS INDICATIVE, and a roadmap item may legitimately carry none
     (a recommendation raised before it has been priced). It seeds the line at 0
     rather than refusing — the quote is a starting point Craig prices, and
     blocking here would make un-priced items the only ones that cannot be
     quoted, which is backwards: those are the ones most likely to need one. */
  const unitPrice = Number(item.cost);

  return makeQuoteRecord({
    seed, now, reserve,
    backlogId: null,
    companyId: String(companyId || ""),
    companyName: companyName || "",
    contactName,
    contactEmail,
    title: item.title,
    /* The budget line's notes are the best description we have of the work, and
       they were written for this client. The quote's own description is the
       place for them; the LINE description stays the short title so the printed
       quote does not repeat a paragraph twice. */
    description: item.detail || "",
    sections: [{
      id: sectionId,
      label: item.title,
      description: item.detail || "",
      selectedOptionId: optionId,
      createdByAI: false,
      options: [{
        id: optionId,
        label: "Standard",
        description: "",
        lineItems: [{
          description: item.title,
          quantity: Number(item.qty) > 0 ? Number(item.qty) : 1,
          unitPrice: Number.isFinite(unitPrice) && unitPrice > 0 ? unitPrice : 0,
        }],
      }],
    }],
    /* Where this quote came from. Not read by the builder today, but it is the
       one chance to record the link — and it is the field a later myITProcess
       sync would join on (Phase 3 explicitly asks that roadmap items carry a
       stable id a sync could map). */
    extra: { roadmapItemId: item.id || null, roadmapSource: item.source || null },
  });
}
